import { ShortCode } from '@highereducation/wordpress';
import { Image } from '@highereducation/hen-ui';

type BackgroundColor = 'neutral-50' | 'neutral-100' | 'white' | 'transparent';

interface ImageShortcodeProps {
  src: string;
  alt: string;
  width?: string;
  height?: string;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'max' | 'true' | '';
  'object-fit'?: 'cover' | 'contain' | 'fill' | 'none';
  'background-color'?: BackgroundColor;
  loading?: 'lazy' | 'eager';
  'fetch-priority'?: 'high' | 'low' | 'auto';
  [key: string]: unknown;
}

const parseRounded = (
  rounded?: string,
): 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'max' | undefined => {
  if (rounded === 'true' || rounded === '') return 'xl';
  if (['none', 'sm', 'md', 'lg', 'xl', 'max'].includes(rounded ?? ''))
    return rounded as 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'max';
  return undefined;
};

// Explicit mapping to ensure Tailwind detects these classes
const backgroundColorClasses: Record<BackgroundColor, string> = {
  'neutral-50': 'bg-neutral-50',
  'neutral-100': 'bg-neutral-100',
  white: 'bg-white',
  transparent: 'bg-transparent',
};

/**
 * ImageShortcode - Shortcode wrapper for Image atomic component
 *
 * Supported shortcode attributes:
 * - src: Image source URL (required)
 * - alt: Alt text for accessibility (required)
 * - width: Image width in pixels
 * - height: Image height in pixels
 * - rounded: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'max' | 'true' - Border radius
 * - object-fit: 'cover' | 'contain' | 'fill' | 'none' - How image fills container
 * - background-color: 'neutral-50' | 'neutral-100' | 'white' | 'transparent' - Background behind image
 * - loading: 'lazy' | 'eager' - Optional. Omitted attributes keep the browser default.
 * - fetch-priority: 'high' | 'low' | 'auto' - Optional. Use `low` for below-the-fold decorative images.
 *
 * @example WordPress shortcode usage:
 * [hen-ui-image src="..." alt="..."]
 * [hen-ui-image src="..." alt="..." rounded="xl"]
 * [hen-ui-image src="..." alt="..." background-color="neutral-50"]
 */
export const ImageShortcode: ShortCode<ImageShortcodeProps> = {
  component: ({
    src,
    alt,
    width,
    height,
    rounded,
    'object-fit': objectFit,
    'background-color': backgroundColor,
    loading,
    'fetch-priority': fetchPriority,
  }) => {
    const image = (
      <Image
        src={src}
        alt={alt}
        width={width ? parseInt(width) : undefined}
        height={height ? parseInt(height) : undefined}
        rounded={parseRounded(rounded)}
        objectFit={objectFit}
        {...(loading ? { loading } : {})}
        {...(fetchPriority ? { fetchPriority } : {})}
      />
    );

    if (backgroundColor && backgroundColorClasses[backgroundColor]) {
      return (
        <div className={backgroundColorClasses[backgroundColor]}>{image}</div>
      );
    }

    return image;
  },
};
